import { Shadow, ToastAlertTypes, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { MetaResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { Box, Stack } from 'native-base';
import React, { useEffect, useMemo, useState } from 'react';
import { FlatList, LayoutChangeEvent } from 'react-native';
import { RecoilState, RecoilValueReadOnly, useRecoilState } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { emptyOffersLoadedState, productsHeightState } from '../../../../../../store/product/atom';
import { emptyCustomFiltersQuery } from '../../../../../../store/product/selectors';
import {
  DynamicAssetsQueryCustomerParams,
  DynamicAssetsQueryParams,
  DynamicCustomFiltersProps,
} from '../../../../../../store/product/types';
import { flatListProps } from '../../../../utils';
import { useGlobalAssetContext } from '../../contexts';
import { AssetsLoading } from '../AssetsLoading';

import { AssetTableHead } from './components/AssetTableHead';
import { ListBottom } from './components/ListBottom';
import { ListEmpty } from './components/ListEmpty';

type ProductsQueryResult<T> = RecoilValueReadOnly<
  | {
      filters?: DynamicCustomFiltersProps['filters'];
      meta: MetaResponse;
      data: T[];
    }
  | undefined
>;

const defaultErrorMessage: [ToastAlertTypes.Type, string, string] = [
  'error',
  'Erro ao carregar os produtos.',
  'Ocorreu um erro ao carregar os produtos,\nverifique sua conexão com a internet e\ntente novamente em instantes.',
];

export namespace AssetTypes {
  export interface Props<T> {
    productsQuery: (param: DynamicAssetsQueryParams) => ProductsQueryResult<T>;
    customFiltersQuery?: (
      param: DynamicAssetsQueryCustomerParams
    ) => RecoilValueReadOnly<DynamicCustomFiltersProps>;
    ProductItem: React.FC<{ offer: T }>;
    ProductItemLoading: React.FC;
    atomToSetProductsLoaded?: RecoilState<{ data: T[]; meta: MetaResponse; loaded: boolean }>;
    keySelector?: keyof T;
  }
}

export const Asset = <T,>({
  productsQuery,
  ProductItem,
  ProductItemLoading,
  atomToSetProductsLoaded,
  customFiltersQuery,
  keySelector,
}: AssetTypes.Props<T>) => {
  const filtersQuery = customFiltersQuery || emptyCustomFiltersQuery;
  const hasFiltersQuery = !!customFiltersQuery;

  const [productsHeight, setProductsHeight] = useRecoilState(productsHeightState);
  const [productsAtomQuery, productsAtomQuerySetter] = useRecoilState(
    atomToSetProductsLoaded || emptyOffersLoadedState
  );
  const { showToast, showToastFromError } = useToastCreator('products-toast');
  const {
    orderBy,
    isDesktop,
    searchTerm,
    currentPage,
    selectedTab,
    cacheControl,
    accountNumber,
    checkingAccountNumber,
    accountId,
    filtersToApply,
    loadingAssets,
    loadingInitialAssets,
    loadingMoreAssets,
    document,
    multipleAccountsIds,
    shouldRefreshAssets,
    setCacheControl,
    setCurrentPage,
    setShouldRefreshAssets,
    setAvailableFilters,
    setLoadingCategories,
    setLoadingAssets,
    setAssetCategories,
  } = useGlobalAssetContext();

  const [allData, setAllData] = useState<T[]>([]);
  const [meta, setMeta] = useState<MetaResponse>();
  const [customFilters, setCustomFilters] = useState<DynamicCustomFiltersProps>();
  const hasRefresher = selectedTab.hasRefresher;

  const { data, loading, refresher, error } = useRecoilQuery(
    productsQuery({
      cacheControl: hasRefresher ? cacheControl : undefined,
      filtersToApply,
      accountNumber,
      checkingAccountNumber,
      accountId,
      document,
      page: currentPage,
      searchTerm,
      orderBy,
      multipleAccountsIds,
    })
  );

  const {
    data: filtersData,
    loading: loadingFilters,
    refresher: filtersRefresher,
  } = useRecoilQuery(filtersQuery({ accountNumber, checkingAccountNumber }), {
    if: () => hasFiltersQuery,
  });

  const handleEndReached = () => {
    if (!meta?.pagination.hasNext || loading || error) {
      return;
    }
    setCurrentPage((prev) => prev + 1);
  };

  const handleCalculateTableHeight = (event: LayoutChangeEvent) => {
    if (productsHeight.heights[selectedTab.id]) {
      return;
    }
    const { height } = event.nativeEvent.layout;
    const calculatedHeight = height * 2;
    setProductsHeight((prev) => ({
      ...prev,
      heights: {
        ...prev.heights,
        [selectedTab.id]: calculatedHeight,
      },
    }));
  };

  const tableMaxHeight = useMemo(() => {
    return productsHeight.heights[selectedTab.id] || 700;
  }, [productsHeight.heights, selectedTab.id]);

  useEffect(() => {
    if (!customFilters) {
      return;
    }
    setAvailableFilters(selectedTab.mountFilters(customFilters));
    if (selectedTab.hasCategories) {
      setAssetCategories(selectedTab.mountCategories(customFilters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFilters]);

  useEffect(() => {
    const isLoadingFilters = hasFiltersQuery
      ? loadingFilters
      : loading && !customFilters?.filters?.length;
    setLoadingCategories(isLoadingFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingFilters, customFilters, loading]);

  useEffect(() => {
    if (loadingFilters || !!customFilters?.filters.length) {
      return;
    }
    const defaultFilters = { filters: data?.filters || [] };
    setCustomFilters(filtersData || defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersData, loadingFilters]);

  useEffect(() => {
    if (shouldRefreshAssets) {
      setAllData([]);
      setMeta(undefined);
      setCurrentPage(1);
      setCacheControl((prev) => prev + 1);
      setShouldRefreshAssets(false);
      if (!hasRefresher) {
        refresher();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefreshAssets]);

  useEffect(() => {
    setMeta(data?.meta);
    setLoadingAssets(loading);
    if (data && data.meta.pagination.page !== currentPage) {
      return;
    }
    if (data && currentPage === 1) {
      setAllData(data.data);
    }
    if (data && currentPage > 1) {
      setAllData((prev) => [...prev, ...data.data]);
    }
    if (data && atomToSetProductsLoaded && !productsAtomQuery.loaded) {
      productsAtomQuerySetter({ ...data, loaded: true });
      filtersRefresher();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  useEffect(() => {
    if (!error) {
      return;
    }
    if (instanceOfErrorListResponse(error)) {
      (error as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
        showToastFromError(errorItem)
      );
      return;
    }

    showToast(...defaultErrorMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const flatListMemoized = useMemo(() => {
    const getKey = (item: T, index: number) => {
      if (keySelector && item[keySelector]) {
        return String(item[keySelector]);
      }
      return index.toString();
    };
    return (
      <Shadow level={2} borderTopRadius={BoxRadius.sm}>
        <VStack maxH={`${tableMaxHeight}px`} overflowY="auto">
          <FlatList
            data={allData}
            {...flatListProps}
            maxToRenderPerBatch={20}
            updateCellsBatchingPeriod={5}
            keyExtractor={getKey}
            showsVerticalScrollIndicator={false}
            ListEmptyComponent={
              <ListEmpty
                hasError={!!error}
                hasData={!!allData.length}
                isLoading={loadingAssets}
                ProductItemLoading={ProductItemLoading}
              />
            }
            contentContainerStyle={{
              marginVertical: Utils.Token.ToInt(Spacing, 'stack.nano'),
            }}
            onEndReached={handleEndReached}
            onEndReachedThreshold={0.2}
            ListFooterComponent={
              <ListBottom
                hasData={!!allData.length}
                hasNextPage={meta?.pagination.hasNext || false}
                isLoading={loadingAssets}
                isLoadingMore={loadingMoreAssets}
                ProductItemLoading={ProductItemLoading}
              />
            }
            ItemSeparatorComponent={() => (
              <Box style={{ height: Utils.Token.ToInt(Spacing, 'stack.nano') }} />
            )}
            renderItem={({ item: offer }) => {
              return <ProductItem offer={offer} />;
            }}
          />
        </VStack>
      </Shadow>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData, loadingAssets, loadingMoreAssets, tableMaxHeight]);

  const renderPrincipal = () => {
    if (loadingInitialAssets) {
      return (
        <Stack onLayout={handleCalculateTableHeight}>
          <AssetsLoading ProductItemLoading={ProductItemLoading} quantity={5} />
        </Stack>
      );
    }

    return flatListMemoized;
  };

  return (
    <VStack>
      {!!isDesktop && <AssetTableHead recurses={selectedTab.tableHead} />}
      {renderPrincipal()}
    </VStack>
  );
};
